import { useState } from "react";
import { Toaster } from "sonner";

// Landing page components
import {
  Navbar,
  HeroSection,
  FeaturesSection,
  HowItWorksSection,
  WhyYASNOSection,
  ROICalculatorSection,
  MultilingualSection,
  TestimonialsSection,
  PricingSection,
  AddOnsSection,
  FAQSection,
  CTASection,
  Footer,
  FloatingActionButton,
  AIResponseModal,
  VideoModal,
  ContactSalesModal
} from "../components/landing";

// Voice Chat Modal
import VoiceChatModal from "../components/landing/VoiceChatModal";
// ROI Calculator Modal
import ROICalculatorModal from "../components/landing/ROICalculatorModal";

// Supported languages
const LANGUAGES = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "ar", name: "العربية", flag: "🇸🇦" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "ur", name: "اردو", flag: "🇵🇰" },
  { code: "zh", name: "中文", flag: "🇨🇳" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "pt", name: "Português", flag: "🇧🇷" },
  { code: "ja", name: "日本語", flag: "🇯🇵" },
  { code: "ko", name: "한국어", flag: "🇰🇷" },
  { code: "hi", name: "हिंदी", flag: "🇮🇳" },
  { code: "ru", name: "Русский", flag: "🇷🇺" },
];

/**
 * LandingPage Component - FIXED ALL ISSUES
 */
export default function LandingPage() {
  // State
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Modal states
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showVoiceChatModal, setShowVoiceChatModal] = useState(false);
  const [showROICalculator, setShowROICalculator] = useState(false);
  const [aiResponse, setAIResponse] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [suggestedCampaign, setSuggestedCampaign] = useState(null);

  // Handle AI response from hero section
  const handleAIResponse = (response, processing, campaign = null) => {
    setAIResponse(response);
    setIsProcessing(processing);
    if (campaign) setSuggestedCampaign(campaign);
  };

  // Close AI response modal
  const closeAIResponse = () => {
    setAIResponse(null);
    setSuggestedCampaign(null);
    setIsProcessing(false);
  };

  // Open voice chat modal (for Try Voice Demo, Try It Yourself, Speak to Agent buttons)
  const openVoiceChat = () => {
    setShowVoiceChatModal(true);
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* Background Effects */}
      <div className="fixed inset-0 bg-gradient-to-b from-background via-background to-black pointer-events-none" />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent pointer-events-none" />
      
      {/* Navigation */}
      <Navbar
        selectedLanguage={selectedLanguage}
        setSelectedLanguage={setSelectedLanguage}
        languages={LANGUAGES}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* Main Content */}
      <main className="relative">
        {/* 1. Hero Section - Try Voice Demo opens voice chat */}
        <HeroSection
          selectedLanguage={selectedLanguage}
          onAIResponse={handleAIResponse}
          onShowVideo={() => setShowVideoModal(true)}
          onOpenVoiceChat={openVoiceChat}
        />
        
        {/* 2. Features Section */}
        <FeaturesSection />
        
        {/* 3. How It Works Section - Try It Yourself opens voice chat */}
        <HowItWorksSection 
          onShowVideo={openVoiceChat}
        />

        {/* 4. Why YASNO Section */}
        <WhyYASNOSection />

        {/* 5. ROI Calculator Section - Opens calculator modal */}
        <ROICalculatorSection onOpenCalculator={() => setShowROICalculator(true)} />

        {/* 6. Multilingual Section */}
        <MultilingualSection />
        
        {/* 7. Testimonials Section */}
        <TestimonialsSection />
        
        {/* 8. Pricing Section - Speak to Agent opens voice chat */}
        <PricingSection 
          onContactSales={() => setShowContactModal(true)}
          onOpenVoiceChat={openVoiceChat}
        />

        {/* 9. Powerful Add-ons Section */}
        <AddOnsSection />
        
        {/* 10. FAQ Section */}
        <FAQSection />

        {/* 11. CTA Section */}
        <CTASection 
          onContactSales={() => setShowContactModal(true)} 
        />
        
        {/* 12. Footer */}
        <Footer />
      </main>

      {/* Floating Action Button */}
      <FloatingActionButton selectedLanguage={selectedLanguage} />

      {/* Modals */}
      <AIResponseModal
        isOpen={aiResponse !== null || isProcessing}
        onClose={closeAIResponse}
        response={aiResponse}
        isProcessing={isProcessing}
        suggestedCampaign={suggestedCampaign}
      />
      
      {/* Video Modal - Shows first video (yasno-demo.mp4) */}
      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
      />
      
      <ContactSalesModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />

      {/* Voice Chat Modal - For Try Voice Demo, Try It Yourself, Speak to Agent */}
      <VoiceChatModal
        isOpen={showVoiceChatModal}
        onClose={() => setShowVoiceChatModal(false)}
        selectedLanguage={selectedLanguage}
      />

      {/* ROI Calculator Modal */}
      <ROICalculatorModal
        isOpen={showROICalculator}
        onClose={() => setShowROICalculator(false)}
      />

      {/* Toast Notifications */}
      <Toaster position="bottom-right" richColors />
    </div>
  );
}
